import { Commentable } from '@/shared/comments/Commentable';
import type { OemAction } from '@/versions/v1/shared/seed/action';
import { SceneSection, SourceLink } from '@/versions/v1/shared/oem-tiles';
import { SeedGlyph } from '../workflows/SeedGlyph';
import { iconForLoop } from '../loops/loop-icons';
import type { AgentChip, LoopTile, ModuleTile, SystemChip } from './Asset360Scene.transform';

// =========================================================================
// Asset360 — the operating model in full. Demo `renderAsset360` (line 2990):
// `mods`, `loops`, `sysl` and `agl`.
//
// Full demo mode only. The simplification pass (B1) folded these four
// sections into the four count cards of "How this works", because in the
// executive room they argued with the hero. In a working session they are the
// point — ten modules, five loops, the systems and the agents, each card
// opening the surface behind it — so Full mode puts them back where the count
// cards were (Tim, 23 Sep). Executive mode never mounts this component.
//
// Presentation only: every row, count and route arrives from
// `Asset360Scene.transform.ts`, and every click resolves through `go`, which
// is `routeForView` in the scene.
//
// UI layer (layer 4). Fork-owned (zone C).
// @see docs/logic/asset360/overview.md
// =========================================================================

export interface Asset360OperatingModelProps {
  modules: readonly ModuleTile[];
  loops: readonly LoopTile[];
  /** How many fleet rules came out of single jobs, and where they live. */
  fleetRules: { count: number; action: OemAction };
  systems: readonly SystemChip[];
  /** The connector count for the Connectors header — twenty-one, not ten. */
  connectorCount: number;
  agents: readonly AgentChip[];
  go: (action: OemAction) => void;
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]';

/** The demo's `.flowlbl` — the ten modules read left to right in three movements. */
const FLOW_LABELS = ['From assets and data', 'To action', 'To value and growth'] as const;

export function Asset360OperatingModel({
  modules,
  loops,
  fleetRules,
  systems,
  connectorCount,
  agents,
  go,
}: Asset360OperatingModelProps) {
  return (
    <>
      {/* -------------------------------------------------------------- */}
      {/* Workflows — the ten module cards                               */}
      {/* -------------------------------------------------------------- */}
      <Commentable name="Modules">
        <SceneSection
          title="Workflows"
          subtitle="ten modules carrying every machine and every job from the data to the action to the value"
          actions={
            <SourceLink onClick={() => go({ view: 'section', sub: 'workflows' })}>
              All modules
            </SourceLink>
          }
        >
          <div
            className="mb-2 hidden grid-cols-3 text-[10px] font-semibold uppercase tracking-[0.1em] @5xl:grid"
            style={{ color: 'var(--ink-tertiary)' }}
            aria-hidden="true"
          >
            {FLOW_LABELS.map((label, i) => (
              <span key={label} className={i === 1 ? 'text-center' : i === 2 ? 'text-right' : ''}>
                {label}
              </span>
            ))}
          </div>
          <ul className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3 @5xl:grid-cols-5">
            {modules.map((m) => (
              <li key={m.id} className="min-w-0">
                <button
                  type="button"
                  onClick={() => go(m.action)}
                  aria-label={`${m.n}. ${m.name}. ${m.jobsLine ?? 'Nothing waiting'}. Agent: ${m.agent}.`}
                  className={`flex h-full w-full min-w-0 flex-col rounded-xl border p-3.5 text-left transition-colors hover:bg-[var(--surface-1)] ${FOCUS_RING}`}
                  style={{
                    borderColor: 'var(--hairline)',
                    borderTop: `3px solid ${m.color}`,
                    background: 'var(--surface-2)',
                  }}
                >
                  <span className="flex items-start gap-2">
                    <span
                      className="mono shrink-0 text-[11px] font-semibold leading-5"
                      style={{ color: m.color }}
                    >
                      {m.n}
                    </span>
                    <span
                      className="min-w-0 flex-1 text-[12.5px] font-semibold leading-5"
                      style={{ color: 'var(--ink-primary)' }}
                    >
                      {m.name}
                    </span>
                    <SeedGlyph markup={m.icon} className="h-5 w-5 shrink-0" stroke={m.color} />
                  </span>
                  <ul
                    className="mt-2 flex-1 space-y-1 text-[11px] leading-[1.45]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    {m.items.map((item) => (
                      <li key={item} className="flex gap-1.5">
                        <span aria-hidden="true" style={{ color: m.color }}>
                          ·
                        </span>
                        <span className="min-w-0">{item}</span>
                      </li>
                    ))}
                    {m.more ? (
                      <li className="pl-3 italic">+{m.more} more</li>
                    ) : null}
                  </ul>
                  <span
                    className="mt-3 border-t pt-2 text-[11.5px]"
                    style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
                  >
                    {m.jobsLine ? (
                      <b className="mono" style={{ color: 'var(--ink-primary)' }}>
                        {m.jobsLine}
                      </b>
                    ) : (
                      <span style={{ color: 'var(--ink-tertiary)' }}>nothing waiting</span>
                    )}
                  </span>
                  <span
                    className="mt-1 truncate text-[10.5px] font-medium"
                    style={{ color: m.color }}
                  >
                    {m.agent}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </SceneSection>
      </Commentable>

      {/* -------------------------------------------------------------- */}
      {/* Feedback loops — the five loop cards and the fleet-rules line  */}
      {/* -------------------------------------------------------------- */}
      <Commentable name="Loops">
        <SceneSection
          title="Feedback loops"
          subtitle="five loops take the outcome of every job back into the front of the model"
          actions={
            <SourceLink onClick={() => go({ view: 'section', sub: 'loops' })}>All loops</SourceLink>
          }
        >
          <ul className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3 @5xl:grid-cols-5">
            {loops.map((loop) => {
              const Icon = iconForLoop(loop.id);
              return (
                <li key={loop.id} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => go(loop.action)}
                    className={`flex h-full w-full min-w-0 flex-col rounded-xl border p-3.5 text-left transition-colors hover:bg-[var(--surface-1)] ${FOCUS_RING}`}
                    style={{
                      borderColor: 'var(--hairline)',
                      borderTop: `3px solid ${loop.color}`,
                      background: 'var(--surface-2)',
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <Icon
                        className="h-4 w-4 shrink-0"
                        style={{ color: loop.color }}
                        aria-hidden="true"
                      />
                      <span
                        className="min-w-0 text-[12.5px] font-semibold leading-5"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {loop.name}
                      </span>
                    </span>
                    <ul
                      className="mt-2 space-y-1 text-[11px] leading-[1.45]"
                      style={{ color: 'var(--ink-tertiary)' }}
                    >
                      {loop.items.map((item) => (
                        <li key={item} className="flex gap-1.5">
                          <span aria-hidden="true" style={{ color: loop.color }}>
                            ·
                          </span>
                          <span className="min-w-0">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                </li>
              );
            })}
          </ul>
          <p
            className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border px-4 py-2.5 text-[11.5px]"
            style={{
              borderColor: 'var(--hairline)',
              background: 'var(--surface-2)',
              color: 'var(--ink-secondary)',
            }}
          >
            <span>
              <span aria-hidden="true">↺ </span>
              Real-world outcomes feed the system — creating a self-improving aftermarket
            </span>
            <span className="ml-auto">
              <b className="mono" style={{ color: 'var(--ink-primary)' }}>
                {fleetRules.count}
              </b>{' '}
              fleet rules now running came out of single jobs ·{' '}
              <SourceLink onClick={() => go(fleetRules.action)}>Fleet learning</SourceLink>
            </span>
          </p>
        </SceneSection>
      </Commentable>

      {/* -------------------------------------------------------------- */}
      {/* Connectors — the ten system families                           */}
      {/* -------------------------------------------------------------- */}
      <Commentable name="Connectors">
        <SceneSection
          title="Connectors"
          subtitle="a unified data foundation across the OEM, customer and third-party systems"
          actions={
            <span
              className="flex items-center gap-2 text-[11px]"
              style={{ color: 'var(--ink-tertiary)' }}
            >
              {connectorCount} connected ·
              <SourceLink onClick={() => go({ view: 'section', sub: 'integration' })}>
                Integration
              </SourceLink>
            </span>
          }
        >
          <ul className="grid grid-cols-1 gap-2 @xl:grid-cols-2 @5xl:grid-cols-5">
            {systems.map((family) => (
              <li key={family.name} className="min-w-0">
                <button
                  type="button"
                  onClick={() => go(family.action)}
                  title={family.systems}
                  className={`flex h-full w-full min-w-0 flex-col rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-[var(--surface-1)] ${FOCUS_RING}`}
                  style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                >
                  <span className="flex flex-wrap items-baseline gap-x-1.5">
                    <b className="text-[12px]" style={{ color: 'var(--ink-primary)' }}>
                      {family.name}
                    </b>
                    <small className="text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                      {family.what}
                    </small>
                  </span>
                  <span
                    className="mt-1 text-[10.5px] leading-[1.45]"
                    style={{ color: 'var(--ink-secondary)' }}
                  >
                    {family.systems}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </SceneSection>
      </Commentable>

      {/* -------------------------------------------------------------- */}
      {/* AI agent layer — one chip per agent                            */}
      {/* -------------------------------------------------------------- */}
      <Commentable name="Agents">
        <SceneSection
          title="AI agent layer"
          subtitle="specialist agents working together to deliver autonomous outcomes"
          actions={
            <span
              className="flex items-center gap-2 text-[11px]"
              style={{ color: 'var(--ink-tertiary)' }}
            >
              {agents.length} agents ·
              <SourceLink onClick={() => go({ view: 'agents' })}>Agent roster</SourceLink>
            </span>
          }
        >
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <ul className="flex min-w-0 flex-1 flex-wrap gap-2">
              {agents.map((agent) => (
                <li key={agent.name}>
                  <button
                    type="button"
                    onClick={() => go(agent.action)}
                    title={agent.gist}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11.5px] font-medium transition-colors hover:bg-[var(--surface-1)] ${FOCUS_RING}`}
                    style={{
                      borderColor: `color-mix(in srgb, ${agent.color} 45%, var(--hairline))`,
                      background: `color-mix(in srgb, ${agent.color} 8%, var(--surface-2))`,
                      color: 'var(--ink-primary)',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{ background: agent.color }}
                    />
                    {agent.name}
                    <small className="mono text-[10px]" style={{ color: 'var(--ink-tertiary)' }}>
                      {agent.level}
                    </small>
                  </button>
                </li>
              ))}
            </ul>
            <p
              className="shrink-0 text-[12px] font-medium leading-[1.6]"
              style={{ color: 'var(--ink-secondary)' }}
            >
              From data to decisions.
              <br />
              From insights to action.
              <br />
              From today to a higher tomorrow.
            </p>
          </div>
        </SceneSection>
      </Commentable>
    </>
  );
}

